﻿import React, { useCallback, useEffect, useState } from 'react';
import { api, Document } from '../services/api';
import { nclayerService } from '../services/nclayer';
import { DocumentDetail } from './DocumentDetail';
import { statusText, useCabinetLang } from '../i18n';

type StatusFilter = 'ALL' | 'PENDING' | 'COMPLETED' | 'CANCELLED' | 'REJECTED';
type SideFilter = 'all' | 'outgoing' | 'incoming';

function lastMonthPeriod(): string {
  const now = new Date();
  const prev = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  return `${prev.getFullYear()}-${String(prev.getMonth() + 1).padStart(2, '0')}`;
}

type Props = {
  title: string;
};

const PAGE_SIZE = 20;
const SIGN_CHUNK_SIZE = 50;

function normalizeBase64(value: string) {
  let s = String(value || '')
    .trim()
    .replace(/-----BEGIN[^-]+-----/g, '')
    .replace(/-----END[^-]+-----/g, '')
    .replace(/\s+/g, '')
    .replace(/-/g, '+')
    .replace(/_/g, '/');
  while (s.length % 4 !== 0) s += '=';
  return s;
}

async function computePdfSha256(base64: string) {
  const normalized = normalizeBase64(base64);
  const binary = atob(normalized);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  const hashBuffer = await crypto.subtle.digest('SHA-256', bytes);
  return Array.from(new Uint8Array(hashBuffer))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

function formatListDt(value?: string) {
  if (!value) return '—';
  return new Date(value).toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function avrNumber(doc: Document) {
  return doc.metadata?.document_number || doc.yandexId || '';
}

function docPeriodParts(doc: Document): { year: number; month: number } | null {
  const y = Number(doc.metadata?.periodYear);
  const m = Number(doc.metadata?.periodMonth);
  if (Number.isFinite(y) && Number.isFinite(m) && m >= 1 && m <= 12) {
    return { year: y, month: m };
  }
  const date = doc.metadata?.document_date || doc.createdAt;
  if (date) {
    const match = String(date).match(/^(\d{4})-(\d{2})/);
    if (match) return { year: Number(match[1]), month: Number(match[2]) };
  }
  return null;
}

function avrPeriodLabel(doc: Document, months: readonly string[]) {
  const parts = docPeriodParts(doc);
  if (parts) return `${months[parts.month - 1]} ${parts.year}`;
  return '';
}

function matchesPeriod(doc: Document, period: string) {
  if (!period) return true;
  const [yStr, mStr] = period.split('-');
  const y = Number(yStr);
  const m = Number(mStr);
  if (!Number.isFinite(y) || !Number.isFinite(m)) return true;
  const parts = docPeriodParts(doc);
  if (!parts) return false;
  return parts.year === y && parts.month === m;
}

function normalizeDocStatus(status: string) {
  if (
    status === 'SIGNED_BY_DRIVER' ||
    status === 'SIGNED_BY_YANDEX' ||
    status === 'SIGNED_BY_ACCOUNTANT'
  ) {
    return 'IN_PROGRESS';
  }
  return status;
}

function waitingKey(doc: Document): string {
  if (doc.waitingKey) return doc.waitingKey;
  const status = normalizeDocStatus(doc.status);
  if (status === 'PROCESSING') return 'PROCESSING';
  if (status === 'CANCELLED') return 'CANCELLED';
  if (status === 'REJECTED') return 'REJECTED';
  if (status === 'COMPLETED') return 'COMPLETED';

  const marks = doc.signatureMarks || {};
  if (marks.DRIVER && marks.YANDEX) return 'COMPLETED';
  if (marks.DRIVER && !marks.YANDEX) return 'WAIT_YANDEX';
  return 'WAIT_DRIVER';
}

function statusColor(doc: Document) {
  switch (waitingKey(doc)) {
    case 'COMPLETED':
    case 'SENT':
      return '#4caf50';
    case 'WAIT_YANDEX':
      return '#ff9800';
    case 'WAIT_DRIVER':
    case 'WAIT_SEND':
      return '#2196f3';
    case 'OVERDUE':
      return '#e64a19';
    case 'REJECTED':
    case 'CANCELLED':
      return '#c62828';
    default:
      return '#9e9e9e';
  }
}

function isSignableStatus(doc: Document) {
  const key = waitingKey(doc);
  return key === 'WAIT_DRIVER' || key === 'WAIT_YANDEX';
}

function avrSideOf(doc: Document): 'yandex' | 'driver' {
  return doc.avrSide === 'driver' ? 'driver' : 'yandex';
}

function matchesStatus(doc: Document, filter: StatusFilter) {
  const key = waitingKey(doc);
  if (filter === 'ALL') return true;
  if (filter === 'PENDING') return key === 'WAIT_DRIVER' || key === 'WAIT_YANDEX' || key === 'WAIT_SEND';
  return key === filter;
}

function isAvr(doc: Document): boolean {
  const cat = String(doc.category || doc.metadata?.category || '')
    .trim()
    .toLowerCase();
  if (cat === 'offer' || cat === 'contract' || cat === 'other') return false;
  if (cat === 'esf' || cat === 'invoice') return false;
  return true;
}

function docTypeLabel(doc: Document, avr: string) {
  const number = avrNumber(doc);
  return number ? `${avr} №${number}` : avr;
}

export const DocumentsPage: React.FC<Props> = ({ title }) => {
  const { t } = useCabinetLang();
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('ALL');
  const [sideFilter, setSideFilter] = useState<SideFilter>('all');
  const [period, setPeriod] = useState(lastMonthPeriod);
  const [documents, setDocuments] = useState<Document[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [selectedDocId, setSelectedDocId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [signing, setSigning] = useState(false);
  const [progress, setProgress] = useState(0);
  const [search, setSearch] = useState('');
  const [pageNum, setPageNum] = useState(1);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const data = await api.getDocuments({
        status: 'ALL',
      });
      setDocuments(data.documents || []);
      setSelected(new Set());
    } catch (err) {
      console.error(err);
      alert(err instanceof Error ? err.message : t.loadError);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [load]);

  useEffect(() => {
    setPageNum(1);
  }, [statusFilter, sideFilter, period, search]);

  useEffect(() => {
    const pending = new URLSearchParams(window.location.search).get('document');
    if (pending) setSelectedDocId(pending);
  }, []);

  const filtered = documents.filter((d) => {
    if (d.status === 'PROCESSING') return false;
    if (!isAvr(d)) return false;
    if (statusFilter !== 'ALL' && !matchesStatus(d, statusFilter)) return false;
    if (sideFilter === 'outgoing' && avrSideOf(d) !== 'yandex') return false;
    if (sideFilter === 'incoming' && avrSideOf(d) !== 'driver') return false;
    if (!matchesPeriod(d, period)) return false;
    if (!search) return true;
    const q = search.toLowerCase();
    return [
      d.id,
      d.driverId,
      d.documentType,
      d.sellerName,
      d.sellerIin,
      d.buyerName,
      d.buyerIin,
      d.metadata?.document_number,
      d.fio,
      d.iin,
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase()
      .includes(q);
  });

  const signable = filtered.filter((d) => isSignableStatus(d));
  const dashDocs = documents.filter((d) => {
    if (d.status === 'PROCESSING') return false;
    if (!isAvr(d)) return false;
    return matchesPeriod(d, period);
  });
  const dashSigned = dashDocs.filter((d) => waitingKey(d) === 'COMPLETED').length;
  const dashPending = dashDocs.filter((d) => {
    const key = waitingKey(d);
    return (
      key === 'WAIT_DRIVER' ||
      key === 'WAIT_YANDEX' ||
      key === 'WAIT_SEND' ||
      key === 'NEW' ||
      key === 'IN_PROGRESS'
    );
  }).length;
  const dashRejectedYandex = dashDocs.filter((d) => {
    const key = waitingKey(d);
    if (key !== 'REJECTED' && key !== 'CANCELLED') return false;
    return avrSideOf(d) !== 'driver';
  }).length;
  const dashRejectedDrivers = dashDocs.filter((d) => {
    const key = waitingKey(d);
    if (key !== 'REJECTED' && key !== 'CANCELLED') return false;
    return avrSideOf(d) === 'driver';
  }).length;
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(pageNum, totalPages);
  const pageItems = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);
  const pageSignableIds = pageItems.filter((d) => isSignableStatus(d)).map((d) => d.id);
  const allSignableIds = signable.map((d) => d.id);
  const allPageSelected =
    pageSignableIds.length > 0 && pageSignableIds.every((id) => selected.has(id));
  const allSelected =
    allSignableIds.length > 0 && allSignableIds.every((id) => selected.has(id));

  const toggle = (id: string) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelected(next);
  };

  const toggleAllSignable = () => {
    if (allSelected) setSelected(new Set());
    else setSelected(new Set(allSignableIds));
  };

  const toggleSelectPage = () => {
    const next = new Set(selected);
    if (allPageSelected) {
      pageSignableIds.forEach((id) => next.delete(id));
    } else {
      pageSignableIds.forEach((id) => next.add(id));
    }
    setSelected(next);
  };

  const handleSign = async () => {
    if (selected.size === 0) {
      alert(t.pickToSign);
      return;
    }
    try {
      setSigning(true);
      await nclayerService.ensureConnected();
    } catch {
      alert(t.ncalayerConnect);
      setSigning(false);
      return;
    }

    const ids = Array.from(selected);
    let ok = 0;
    let canceled = false;
    const signErrors: string[] = [];

    for (let batchIdx = 0; batchIdx < ids.length; batchIdx += SIGN_CHUNK_SIZE) {
      const chunk = ids.slice(batchIdx, batchIdx + SIGN_CHUNK_SIZE);
      setProgress(Math.round((ok / ids.length) * 100));

      const items: Array<{ documentId: string; pdfBase64: string; pdfSha256: string }> = [];
      for (const docId of chunk) {
        try {
          const doc = await api.getDocument(docId);
          if (!doc.pdfBase64) {
            signErrors.push(`${docId.slice(0, 8)}…: ${t.noPdf}`);
            continue;
          }
          items.push({
            documentId: docId,
            pdfBase64: doc.pdfBase64,
            pdfSha256: await computePdfSha256(doc.pdfBase64),
          });
        } catch (err) {
          signErrors.push(
            `${docId.slice(0, 8)}…: ${err instanceof Error ? err.message : t.loadError}`
          );
        }
      }

      if (!items.length) continue;

      const batchResult = await nclayerService.signAttachedBatch(
        items.map((item) => item.pdfBase64),
        (current) => {
          const done = ok + current;
          setProgress(Math.round((Math.min(done, ids.length) / ids.length) * 100));
        }
      );

      if (batchResult.canceledByUser) {
        canceled = true;
        break;
      }
      if (!batchResult.success) {
        signErrors.push(batchResult.error || t.ncalayerError);
        continue;
      }

      let signatureByDocumentId = new Map<string, string>();
      try {
        const matchRes = await api.matchSignatures({
          items: items.map((item) => ({
            documentId: item.documentId,
            pdfSha256: item.pdfSha256,
          })),
          signatures: batchResult.signatures,
        });
        if (matchRes.matched.length === items.length) {
          signatureByDocumentId = new Map(
            matchRes.matched.map((row) => [
              row.documentId,
              batchResult.signatures[row.signatureIndex],
            ])
          );
        } else {
          (matchRes.errors || []).forEach((row) => {
            signErrors.push(row.error || `${row.documentId.slice(0, 8)}…`);
          });
          continue;
        }
      } catch (err) {
        signErrors.push(err instanceof Error ? err.message : t.ncalayerError);
        continue;
      }

      const signedAt = new Date().toISOString();
      for (const item of items) {
        const signature = signatureByDocumentId.get(item.documentId);
        if (!signature) {
          signErrors.push(`${item.documentId.slice(0, 8)}…`);
          continue;
        }
        try {
          await api.signDocument(item.documentId, {
            signature,
            cmsAttached: signature,
            certificateThumbprint: 'ncalayer-cms',
            signedAt,
          });
          ok += 1;
          setProgress(Math.round((ok / ids.length) * 100));
        } catch (err) {
          signErrors.push(
            `${item.documentId.slice(0, 8)}…: ${err instanceof Error ? err.message : t.loadError}`
          );
        }
      }
    }

    setProgress(100);
    if (canceled) {
      alert(`${t.canceledInNca}. ${t.signedCount} ${ok} ${t.of} ${ids.length}`);
    } else if (signErrors.length && ok === 0) {
      alert(signErrors[0]);
    } else {
      alert(
        `${t.signedCount} ${ok} ${t.of} ${ids.length}${
          signErrors.length ? `\n${signErrors[0]}` : ''
        }`
      );
    }
    setSigning(false);
    setProgress(0);
    await load();
  };

  const statusLabel = (doc: Document) => statusText(t, waitingKey(doc));

  if (selectedDocId) {
    return <DocumentDetail documentId={selectedDocId} onBack={() => setSelectedDocId(null)} />;
  }

  return (
    <>
      <div className="section-header">
        <h2>{title}</h2>
        <span className="doc-count">
          {filtered.length} {t.pcs}
        </span>
      </div>

      <div className="mini-dash avr-mini-dash">
        <div className="overview-metric">
          <div className="overview-metric-label">{t.signedCount}</div>
          <div className="overview-metric-value tone-ok">{dashSigned}</div>
        </div>
        <div className="overview-metric">
          <div className="overview-metric-label">{t.pending}</div>
          <div className="overview-metric-value tone-wait">{dashPending}</div>
        </div>
        <div className="overview-metric">
          <div className="overview-metric-label">{t.rejectedByYandex}</div>
          <div className="overview-metric-value tone-warn">{dashRejectedYandex}</div>
        </div>
        <div className="overview-metric">
          <div className="overview-metric-label">{t.rejectedByDrivers}</div>
          <div className="overview-metric-value tone-warn">{dashRejectedDrivers}</div>
        </div>
      </div>

      <div className="filters-panel">
        <div className="filter-row">
          <div className="filter-group">
            <label>{t.period}</label>
            <input
              type="month"
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
              aria-label={t.period}
            />
          </div>
          <div className="filter-group">
            <label>{t.avr}</label>
            <select
              value={sideFilter}
              onChange={(e) => setSideFilter(e.target.value as SideFilter)}
            >
              <option value="all">{t.all}</option>
              <option value="outgoing">{t.outgoing}</option>
              <option value="incoming">{t.incoming}</option>
            </select>
          </div>
          <div className="filter-group">
            <label>{t.status}</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
            >
              <option value="ALL">{t.all}</option>
              <option value="PENDING">{t.pending}</option>
              <option value="COMPLETED">{t.signed}</option>
              <option value="CANCELLED">{t.cancelled}</option>
              <option value="REJECTED">{t.rejected}</option>
            </select>
          </div>
          <div className="filter-group filter-iin">
            <label>{t.search}</label>
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t.searchPlaceholder}
            />
          </div>
          {period && (
            <button type="button" className="btn-reset" onClick={() => setPeriod('')}>
              {t.resetPeriod}
            </button>
          )}
          <button
            type="button"
            className="btn-apply"
            disabled={selected.size === 0 || signing}
            onClick={() => void handleSign()}
          >
            {signing ? `${t.signing} ${progress}%` : `${t.sign} (${selected.size})`}
          </button>
        </div>
      </div>

      <div className="docs-toolbar">
        <button
          type="button"
          className="btn-reset"
          disabled={pageSignableIds.length === 0}
          onClick={toggleSelectPage}
        >
          {allPageSelected ? t.unselectPage : t.selectPage}
        </button>
        <button
          type="button"
          className="btn-reset"
          disabled={allSignableIds.length === 0}
          onClick={toggleAllSignable}
        >
          {allSelected ? t.unselectAll : t.selectAll}
        </button>
        {selected.size > 0 && (
          <span className="doc-count">
            {t.selected}: {selected.size}
          </span>
        )}
      </div>

      <div className="documents-section">
        {loading ? (
          <div className="no-documents">{t.loading}</div>
        ) : filtered.length === 0 ? (
          <div className="no-documents">{t.noDocuments}</div>
        ) : (
          <div className="table-wrapper">
          <table className="documents-table docs-wide">
            <thead>
              <tr>
                <th style={{ width: 40 }}>
                  <input
                    type="checkbox"
                    checked={allPageSelected}
                    onChange={toggleSelectPage}
                    disabled={pageSignableIds.length === 0}
                    aria-label={t.selectPage}
                  />
                </th>
                <th>{t.type}</th>
                <th>{t.customer}</th>
                <th>{t.executor}</th>
                <th>{t.status}</th>
              </tr>
            </thead>
            <tbody>
              {pageItems.map((doc) => (
                <tr key={doc.id} onClick={() => setSelectedDocId(doc.id)}>
                  <td onClick={(e) => e.stopPropagation()}>
                    {isSignableStatus(doc) ? (
                      <input
                        type="checkbox"
                        checked={selected.has(doc.id)}
                        onChange={() => toggle(doc.id)}
                      />
                    ) : null}
                  </td>
                  <td className="doc-name">
                    {docTypeLabel(doc, t.avr)}
                    <div className="period">
                      {avrPeriodLabel(doc, t.months) || formatListDt(doc.createdAt)}
                    </div>
                  </td>
                  <td className="fio">
                    {doc.buyerName || doc.metadata?.buyer?.name || doc.fio || '—'}
                    <div className="iin">
                      {doc.buyerIin ||
                        doc.metadata?.buyer?.iin ||
                        doc.iin ||
                        doc.metadata?.iin ||
                        '—'}
                    </div>
                  </td>
                  <td className="fio">
                    {doc.sellerName || doc.metadata?.seller?.name || '—'}
                    <div className="iin">
                      {doc.sellerIin || doc.metadata?.seller?.iin || '—'}
                    </div>
                  </td>
                  <td>
                    <span className="status-badge" style={{ background: statusColor(doc) }}>
                      {statusLabel(doc)}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {totalPages > 1 && (
            <div className="pagination">
              <button
                type="button"
                className="btn-reset"
                disabled={currentPage <= 1}
                onClick={() => setPageNum(currentPage - 1)}
              >
                {t.prev}
              </button>
              <span className="doc-count">
                {currentPage} / {totalPages}
              </span>
              <button
                type="button"
                className="btn-reset"
                disabled={currentPage >= totalPages}
                onClick={() => setPageNum(currentPage + 1)}
              >
                {t.next}
              </button>
            </div>
          )}
          </div>
        )}
      </div>
    </>
  );
};
